import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getCurrentLicense, createCheckoutSession, getBillingPortalUrl } from "@/api/licenses";
import { toast } from "sonner";
import { ApiError } from "@/api/types";

export function useCurrentLicense() {
  return useQuery({
    queryKey: ["user-license"],
    queryFn: getCurrentLicense,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnMount: false, // Only refetch if data is stale
    retry: (failureCount, error: any) => {
      // Don't retry on auth errors
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

interface CheckoutOptions {
  openInModal?: boolean;
}

export function useCreateCheckoutSession(options: CheckoutOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation<{ checkout_url: string; session_id: string }, ApiError>({
    mutationFn: createCheckoutSession,
    onSuccess: (data) => {
      if (options.openInModal) {
        // Open in a new window/tab (modal-like behavior)
        const popup = window.open(
          data.checkout_url,
          'stripe-checkout',
          'width=800,height=600,scrollbars=yes,resizable=yes'
        );

        // Optional: Listen for the popup to close and refresh license data
        if (popup) {
          const checkClosed = setInterval(() => {
            if (popup.closed) {
              clearInterval(checkClosed);
              // Invalidate license query to refetch updated data
              queryClient.invalidateQueries({ queryKey: ["user-license"] });
            }
          }, 1000);
        }
      } else {
        // Redirect to Stripe checkout (default behavior)
        window.location.href = data.checkout_url;
      }
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create checkout session");
    },
  });
}

export function useGetBillingPortal() {
  return useMutation<{ portal_url: string }, ApiError>({
    mutationFn: getBillingPortalUrl,
    onSuccess: (data) => {
      // Open billing portal in new tab
      window.open(data.portal_url, "_blank");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to access billing portal");
    },
  });
}
